import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {type WBMenuOnSelectHandler} from '@wandb/weave/common/components/WBMenu';
import {type SetState} from '@wandb/weave/common/util/types';
import React from 'react';
import {Button, Menu} from 'semantic-ui-react';

import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  getShownRowsRange,
  usePageEffects,
} from './util';
import type {WBReactTableProps} from './WBReactTable';
import * as S from './WBReactTable.styles';

export type WBReactTablePaginationProps = Pick<
  WBReactTableProps,
  'pageSizeOptions' | 'hasNextPage' | 'fetchingNextPage' | 'onFetchNextPage'
> & {
  page: number;
  pageSize: number;
  rowCount: number;
  changePageSize: (newPageSize: number) => void;
  setPage: SetState<number>;
  extraActions?: React.ReactNode;
  hideRowSummary?: boolean;
};

export const WBReactTablePagination: React.FC<WBReactTablePaginationProps> =
  React.memo(
    ({
      page,
      pageSize,
      pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
      rowCount,
      changePageSize,
      hasNextPage = false,
      fetchingNextPage = false,
      onFetchNextPage,
      setPage,
      extraActions,
      hideRowSummary = false,
    }) => {
      const {setAwaitingMoreData} = usePageEffects({
        rowCount,
        pageSize,
        setPage,
      });

      // We need to manually calculate the pagination canPrevious and canNext
      // values because we don't use ReactTable's provided pagination component
      const totalPages = Math.ceil(rowCount / pageSize);
      const pageWithMax = Math.min(page, totalPages - 1);
      const canPrevious = pageWithMax > 0;
      const canNext = pageWithMax < totalPages - 1;

      return (
        <Menu.Menu position="right">
          {extraActions && <S.MenuItem>{extraActions}</S.MenuItem>}
          {!hideRowSummary && (
            <S.MenuItem>
              <S.RowCountSelectWrapper>
                <S.RowCountSelect
                  menuTheme="light"
                  menuFontSize={13}
                  options={pageSizeOptions.map(size => ({
                    name: `${size}`,
                    value: size,
                    icon: null,
                  }))}
                  value={pageSize}
                  displayedValue={getShownRowsRange({
                    rowCount,
                    page,
                    pageSize,
                  })}
                  autoMenuWidth
                  onSelect={changePageSize as WBMenuOnSelectHandler}
                />
                {' of '}
                {rowCount}
                {hasNextPage && '+'}
              </S.RowCountSelectWrapper>
            </S.MenuItem>
          )}
          <S.MenuItem>
            <S.PageNav>
              <Button.Group>
                <Button
                  size="tiny"
                  className="wb-icon-button only-icon"
                  disabled={!canPrevious || fetchingNextPage}
                  onClick={() => setPage(prev => prev - 1)}>
                  <LegacyWBIcon name="previous" />
                </Button>
                <Button
                  size="tiny"
                  className="wb-icon-button only-icon"
                  disabled={
                    !(canNext || (onFetchNextPage != null && hasNextPage)) ||
                    fetchingNextPage
                  }
                  loading={fetchingNextPage}
                  onClick={() => {
                    if (canNext) {
                      setPage(prev => prev + 1);
                    } else if (onFetchNextPage != null && hasNextPage) {
                      onFetchNextPage();
                      setAwaitingMoreData(true);
                    }
                  }}>
                  <LegacyWBIcon
                    name="next"
                    style={{
                      visibility: fetchingNextPage ? 'hidden' : 'visible',
                    }}
                  />
                </Button>
              </Button.Group>
            </S.PageNav>
          </S.MenuItem>
        </Menu.Menu>
      );
    }
  );
